import React, { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

export interface SplineViewerInstance {
  id: string
  element: HTMLElement | null
  isLoaded: boolean
  isReady: boolean
}

export interface SplineViewerContextType {
  isScriptLoaded: boolean
  viewerInstances: Map<string, SplineViewerInstance>
  registerViewer: (id: string) => void
  setViewerElement: (id: string, element: HTMLElement | null) => void
  markViewerLoaded: (id: string) => void
  markViewerReady: (id: string) => void
  areAllViewersReady: () => boolean
  getViewerInstance: (id: string) => SplineViewerInstance | null
}

const SplineViewerContext = createContext<SplineViewerContextType | undefined>(undefined)

export const SplineViewerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [viewerInstances, setViewerInstances] = useState<Map<string, SplineViewerInstance>>(new Map())
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)
  const scriptLoadedRef = useRef(false)

  // Load the Spline viewer script
  useEffect(() => {
    if (scriptLoadedRef.current) {
      return
    }

    const existingScript = document.querySelector('script[src*="@splinetool/viewer"]')
    if (existingScript) {
      setIsScriptLoaded(true)
      scriptLoadedRef.current = true
      return
    }

    const script = document.createElement('script')
    script.type = 'module'
    script.src = 'https://unpkg.com/@splinetool/viewer@1.10.44/build/spline-viewer.js'
    script.onload = () => {
      // Check if custom element is defined
      setTimeout(() => {
        const isCustomElementDefined = customElements.get('spline-viewer')
        if (!isCustomElementDefined) {
          console.error('spline-viewer custom element not defined after script load')
        }
      }, 100)

      setIsScriptLoaded(true)
      scriptLoadedRef.current = true
    }
    script.onerror = (error) => {
      console.error('Failed to load Spline viewer script:', error)
    }

    document.head.appendChild(script)

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script)
      }
    }
  }, [])

  const registerViewer = useCallback((id: string) => {
    setViewerInstances((prev) => {
      const newMap = new Map(prev)
      if (!newMap.has(id)) {
        newMap.set(id, {
          id,
          element: null,
          isLoaded: false,
          isReady: false,
        })
      }
      return newMap
    })
  }, [])

  const setViewerElement = useCallback((id: string, element: HTMLElement | null) => {
    setViewerInstances((prev) => {
      const newMap = new Map(prev)
      const instance = newMap.get(id)
      if (instance) {
        newMap.set(id, { ...instance, element })
      }
      return newMap
    })
  }, [])

  const markViewerLoaded = useCallback((id: string) => {
    setViewerInstances((prev) => {
      const newMap = new Map(prev)
      const instance = newMap.get(id)
      if (instance) {
        newMap.set(id, { ...instance, isLoaded: true })
      }
      return newMap
    })
  }, [])

  const markViewerReady = useCallback((id: string) => {
    setViewerInstances((prev) => {
      const newMap = new Map(prev)
      const instance = newMap.get(id)
      if (instance) {
        newMap.set(id, { ...instance, isReady: true })
      } else {
        // Allow early-ready before registration
        newMap.set(id, {
          id,
          element: null,
          isLoaded: true,
          isReady: true,
        })
      }
      return newMap
    })
  }, [])

  const areAllViewersReady = useCallback(() => {
    if (viewerInstances.size === 0) {
      return false
    }
    for (const instance of viewerInstances.values()) {
      if (!instance.isReady) {
        return false
      }
    }
    return true
  }, [viewerInstances])

  const getViewerInstance = useCallback(
    (id: string) => {
      return viewerInstances.get(id) || null
    },
    [viewerInstances]
  )

  const contextValue: SplineViewerContextType = {
    isScriptLoaded,
    viewerInstances,
    registerViewer,
    setViewerElement,
    markViewerLoaded,
    markViewerReady,
    areAllViewersReady,
    getViewerInstance,
  }
  return <SplineViewerContext.Provider value={contextValue}>{children}</SplineViewerContext.Provider>
}

export const useSplineViewer = (): SplineViewerContextType => {
  const ctx = useContext(SplineViewerContext)
  if (!ctx) {
    throw new Error('useSplineViewer must be used within a SplineViewerProvider')
  }
  return ctx
}
